import React from 'react';
import { Card, Row, Col, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PerformanceMetrics as MetricsType } from './types';
import { formatTime, getRating, getRatingClass } from './utils';
import { benchmarks } from './constants';

interface PerformanceMetricsProps {
  metrics: MetricsType | null;
}

const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({ metrics }) => {
  const { t } = useTranslation('performance');
  
  return (
    <Card>
      <Card.Header className="bg-info text-white">
        <h5 className="mb-0">{t('metrics.title', 'Performance Metrics')}</h5>
      </Card.Header>
      <Card.Body>
        {!metrics ? (
          <div className="text-center text-muted">
            <p>{t('metrics.runTestPrompt', 'Run a test to see performance metrics')}</p>
          </div>
        ) : (
          <Row>
            <Col md={3}>
              <h6 className="border-bottom pb-2 mb-3">{t('metrics.responseTime.title', 'Response Time')}</h6>
              <div className="mb-3">
                <div>
                  <strong>{t('metrics.responseTime.average', 'Average Response Time:')}</strong> 
                  <div className="mt-1 mb-1">
                    {formatTime(metrics.averageResponseTime)}
                    <span className={`ms-2 ${getRatingClass(getRating('responseTime', metrics.averageResponseTime))}`}>
                      {getRating('responseTime', metrics.averageResponseTime)}
                    </span>
                  </div>
                  <div className="text-muted small">
                    {t('metrics.responseTime.target', 'Target: <{{good}}ms (good), <{{excellent}}ms (excellent)', {
                      good: benchmarks.responseTime.good,
                      excellent: benchmarks.responseTime.excellent
                    })}
                  </div>
                </div>
                <div className="mt-2">
                  <strong>{t('metrics.responseTime.max', 'Max Response Time:')}</strong>
                  <div className="mt-1">{formatTime(metrics.maxResponseTime)}</div>
                </div>
              </div>
            </Col>

            <Col md={3}>
              <h6 className="border-bottom pb-2 mb-3">{t('metrics.successRate.title', 'Success Rate')}</h6>
              <div className="mb-3">
                <div>
                  <strong>{t('metrics.successRate.rate', 'Success Rate:')}</strong>
                  <div className="mt-1 mb-1">
                    {metrics.successRate.toFixed(2)}%
                    <span className={`ms-2 ${getRatingClass(getRating('successRate', metrics.successRate))}`}>
                      {getRating('successRate', metrics.successRate)}
                    </span>
                  </div>
                  <div className="text-muted small">
                    {t('metrics.successRate.target', 'Target: >{{good}}% (good), >{{excellent}}% (excellent)', {
                      good: benchmarks.successRate.good,
                      excellent: benchmarks.successRate.excellent
                    })}
                  </div>
                </div>
                <div className="mt-2">
                  <strong>{t('metrics.successRate.failed', 'Failed Requests:')}</strong>
                  <div className="mt-1">{metrics.failedClicks}</div>
                </div>
              </div>
            </Col>

            <Col md={3}>
              <h6 className="border-bottom pb-2 mb-3">{t('metrics.throughput.title', 'Throughput')}</h6>
              <div className="mb-3">
                <div>
                  <strong>{t('metrics.throughput.clicksPerSecond', 'Clicks Per Second:')}</strong>
                  <div className="mt-1 mb-1">
                    {metrics.clicksPerSecond.toFixed(2)}
                    <span className={`ms-2 ${getRatingClass(getRating('throughput', metrics.clicksPerSecond))}`}>
                      {getRating('throughput', metrics.clicksPerSecond)}
                    </span>
                  </div>
                  <div className="text-muted small">
                    {t('metrics.throughput.typical', 'Typical: {{typical}}/s, High: {{high}}/s', {
                      typical: benchmarks.throughput.typical,
                      high: benchmarks.throughput.high
                    })}
                  </div>
                </div>
                <div className="mt-2">
                  <strong>{t('metrics.throughput.totalClicks', 'Total Clicks:')}</strong>
                  <div className="mt-1">{metrics.totalClicks}</div>
                </div>
              </div>
            </Col>

            <Col md={3}>
              <h6 className="border-bottom pb-2 mb-3">{t('metrics.batchProcessing.title', 'Batch Processing')}</h6>
              <div className="mb-3">
                <div>
                  <strong>{t('metrics.batchProcessing.lastBatchSize', 'Last Batch Size:')}</strong>
                  <div className="mt-1">{metrics.lastBatchSize}</div>
                </div>
                <div className="mt-2">
                  <strong>{t('metrics.batchProcessing.time', 'Batch Processing Time:')}</strong>
                  <div className="mt-1">
                    {metrics.averageBatchProcessingTime
                      ? `${metrics.averageBatchProcessingTime.toFixed(2)}ms`
                      : 'N/A'}
                  </div>
                  <div className="text-muted small">
                    {t('metrics.batchProcessing.target', 'Target: <{{excellent}}ms', {
                      excellent: benchmarks.batchProcessing.excellentTime
                    })}
                  </div>
                </div>
              </div>
            </Col>

            <Col md={12} className="mt-3">
              <Alert variant="info">
                <strong>{t('metrics.analysis.title', 'Analysis:')}</strong><br/>
                {metrics.successRate < benchmarks.successRate.acceptable && (
                  <span>{t('metrics.analysis.lowSuccessRate', 'The success rate is below acceptable levels. Consider reducing batch size or increasing interval.')}</span>
                )}
                {metrics.averageResponseTime > benchmarks.responseTime.acceptable && (
                  <span>{t('metrics.analysis.highResponseTime', 'Response times are too high. Consider optimizing server processing or increasing batch interval.')}</span>
                )}
                {metrics.successRate >= benchmarks.successRate.good && metrics.averageResponseTime <= benchmarks.responseTime.good && (
                  <span>{t('metrics.analysis.good', 'Performance is good! Current configuration is working well.')}</span>
                )}
              </Alert>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default PerformanceMetrics; 